import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Component, ElementRef, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';
import {
  ButtonActionType,
  AndOrOperator,
  Outcome,
  OutcomeAnswer,
  OutcomeOperator,
  Widget,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetState,
} from '../../widgets.model';
import WidgetComponentHelpers from '../../widget-component-helpers';
import { LanguageService } from '../../../core/language.service';
import { WidgetEditorDataService } from '../../widget-editor-data.service';
import { WidgetService } from '../../../shared/services/widget.service';
import CampaignTemplateHelpers from '../../templates/campaign-template-helpers';
import { Logger, LoggerFactory } from '../../../core/logger.service';
import { OutcomeLogicService } from '../outcome-logic.service';
import { OutcomeLogicTesterService } from './outcome-logic-tester.service';
import { PeopleService } from '../../../people/people.service';
import { CustomProfileProperty } from '../../../back-office/account.model';
import { ProfileFieldEditingComponent } from '../../../people/contact-fields/profile-field-editing/profile-field-editing.component';
import { OutcomeGeneratorComponent, OutcomeGeneratorData } from './outcome-generator/outcome-generator.component';
import { AccountLanguageService } from '../../../core/account-language.service';
import { SubscriptionHandlerBaseComponent } from '../../../core/subscription-handler.base';
import { CurrentContextService } from '../../../core/current-context.service';
import { ConfirmationDialogService } from '../../../shared/confirmation-dialog/confirmation-dialog.service';
import { ConfirmationDialogData } from '../../../shared/confirmation-dialog/confirmation-dialog-config';

@Component({
  selector: 'app-outcome-logic',
  templateUrl: './outcome-logic.component.html',
  styleUrl: './outcome-logic.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OutcomeLogicComponent extends SubscriptionHandlerBaseComponent {
  private readonly logger: Logger;
  outcomes: Outcome[] = [];
  expandedOutcomeIndex: number | null = null;
  @ViewChild('nameInput') nameInputRef!: ElementRef;

  loading: boolean = true;
  widget: Widget;
  availableStates: WidgetState[] = [];
  inputComponents: {
    name: string;
    displayName: string;
    values: string[];
    valuesWithScore: { value: string; score: number; isCorrect: boolean }[];
    isMultiSelect: boolean;
  }[] = [];
  regularInputs: {
    name: string;
    displayName: string;
    values: string[];
    valuesWithScore: { value: string; score: number; isCorrect: boolean }[];
    isMultiSelect: boolean;
  }[] = [];
  scoreInput: {
    name: string;
    displayName: string;
    values: string[];
    valuesWithScore: { value: string; score: number; isCorrect: boolean }[];
    isMultiSelect: boolean;
  } | null = null;
  OutcomeOperator = OutcomeOperator;
  WidgetComponentType = WidgetComponentType;
  AndOrOperator = AndOrOperator;
  outcomesAreNotAllowed: string = null;
  saveIsAllowed: boolean = false;
  profileFields: CustomProfileProperty[];

  outcomeAnswerGroups: { answers: OutcomeAnswer[]; startIndex: number }[][] = [];

  matchedOutcomeIndex: number | null = null;
  showTestLogic: boolean = false;
  minScore: number = 0;
  maxScore: number = 0;
  hasScore: boolean = false;

  missingScoresRanges: string = '';
  missingScoresIsDefault: boolean = false;
  missingScores: number[] = [];
  logicUpdateTrigger: number = 0;

  constructor(
    private readonly loggerFactory: LoggerFactory,
    public dialogRef: MatDialogRef<OutcomeLogicComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { widget: Widget; addOutcomeOnOpen?: boolean },
    private dialog: MatDialog,
    private readonly languageService: LanguageService,
    private readonly widgetEditorDataService: WidgetEditorDataService,
    private readonly widgetService: WidgetService,
    private readonly outcomeLogicService: OutcomeLogicService,
    private readonly outcomeLogicTesterService: OutcomeLogicTesterService,
    private readonly peopleService: PeopleService,
    private readonly accountLanguageService: AccountLanguageService,
    private readonly currentContextService: CurrentContextService,
    private readonly confirmationDialogService: ConfirmationDialogService,
  ) {
    super();
    this.logger = loggerFactory.get('OutcomeLogicComponent');
    this.widget = data.widget;
    this.outcomes = this.widget.outcomes || [];
    this.outcomesAreNotAllowed = this.shouldNotAllowOutcomes();
    if (!this.outcomesAreNotAllowed) {
      this.availableStates = [...this.widget.states]
        .sort((a, b) => a.order - b.order)
        .map((item) => {
          return {
            ...item,
            titleState: WidgetComponentHelpers.pickTitleForState(item, this.languageService),
          };
        });
      this.inputComponents = this.outcomeLogicService.getInputComponents(this.widget);
      this.regularInputs = this.inputComponents.filter((c) => c.name.toLowerCase() !== 'score');
      this.scoreInput = this.inputComponents.find((c) => c.name.toLowerCase() === 'score') || null;
    }
    this.parseAnswerValuesFromString();
    this.calculateOutcomeProperties();
    this.getProfileFields();
    this.calculateScoreRange();
    this.calculateMissingScores();
    this.saveIsAllowed = true;
    this.loading = false;

    if (this.data.addOutcomeOnOpen) {
      this.addOutcome();
    }
  }

  private parseAnswerValuesFromString() {
    this.outcomes.forEach((outcome) => {
      outcome.answers.forEach((answer) => {
        // Convert string to array if it's a comma-separated string and has available values
        if (this.isMultipleSelect(answer) && typeof answer.value === 'string' && answer.value) {
          try {
            // Try parsing as JSON first
            answer.value = JSON.parse(answer.value);
          } catch {
            // If not JSON, split by comma
            answer.value = answer.value
              .toString()
              .split(',')
              .map((v) => v.trim())
              .filter((v) => v);
          }
        }
      });
    });
  }

  private calculateOutcomeProperties() {
    this.matchedOutcomeIndex = null;
    this.outcomeAnswerGroups = this.outcomes.map((o) => this.getAnswerGroups(o));
    this.outcomes.forEach((outcome, index) => {
      // Calculate canDrag
      outcome.canDrag = !outcome.isDefault;

      // Calculate displayName
      outcome.displayName = this.outcomeLogicService.getOutcomeDisplayName(outcome, index, this.inputComponents);

      // Calculate availableValues for each answer
      outcome.answers.forEach((answer) => {
        const component = this.inputComponents.find((comp) => comp.name === answer.question);
        answer.availableValues = component?.values || [];

        // Convert value to array for multi-select to prevent "Value must be an array in multiple-selection mode"
        if (answer.availableValues.length > 0 && this.isMultipleSelect(answer) && !Array.isArray(answer.value)) {
          if (typeof answer.value === 'string' && answer.value) {
            try {
              answer.value = JSON.parse(answer.value);
            } catch {
              answer.value = (answer.value as string)
                .split(',')
                .map((v) => v.trim())
                .filter((v) => v);
            }
          } else {
            answer.value = [];
          }
        }
      });
    });
  }

  private calculateScoreRange() {
    const result = this.outcomeLogicTesterService.calculateScoreRange(this.widget, this.scoreInput?.name || null);
    this.minScore = result.min;
    this.maxScore = result.max;
    this.hasScore = result.hasScore;
  }

  onDragDrop(event: CdkDragDrop<Outcome[]>) {
    // Don't allow moving the default outcome
    const draggedItem = this.outcomes[event.previousIndex];
    const targetItem = this.outcomes[event.currentIndex];

    if (draggedItem.isDefault || targetItem.isDefault) {
      return;
    }

    // Move item in array
    moveItemInArray(this.outcomes, event.previousIndex, event.currentIndex);

    // Move state on widget
    this.moveStateToOutcomeOrder(draggedItem.stateId, targetItem.stateId);

    // Update expanded index if needed
    if (this.expandedOutcomeIndex === event.previousIndex) {
      this.expandedOutcomeIndex = event.currentIndex;
    } else if (this.expandedOutcomeIndex === event.currentIndex) {
      this.expandedOutcomeIndex = event.previousIndex;
    }

    // Recalculate properties and save
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
    this.save().subscribe();
  }

  onExpansionToggle(index: number, isExpanded: boolean) {
    if (isExpanded) {
      this.expandedOutcomeIndex = index;
    } else if (this.expandedOutcomeIndex === index) {
      this.expandedOutcomeIndex = null;
    }
  }

  isOutcomeExpanded(index: number): boolean {
    return this.expandedOutcomeIndex === index;
  }
  private shouldNotAllowOutcomes(): string {
    if (this.outcomes.length > 0) {
      return null;
    }
    // Is realtime
    if (this.widget.formSettings?.success) {
      return 'not-allowed-if-realtime';
    }
    // Has "Change step"
    const submitButton = WidgetComponentHelpers.getSubmitButtonFromWidget(this.widget);
    if (submitButton?.actionType === ButtonActionType.SwitchState) {
      return 'not-allowed-if-message';
    }
    // Has success step
    if (this.widget.states.some((state) => state.type === WidgetComponentType.Success)) {
      return 'not-allowed-if-success-step';
    }
  }

  hasDefaultOutcome(): boolean {
    return this.outcomes.some((outcome) => outcome.isDefault);
  }

  addDefaultOutcome() {
    this.addState('Default outcome').subscribe((createdState) => {
      const defaultStateId = createdState.id;
      this.outcomes.push({
        stateId: defaultStateId,
        answers: [],
        isDefault: true,
      });
      this.expandedOutcomeIndex = this.outcomes.length - 1;
      this.calculateOutcomeProperties();
      this.calculateMissingScores();
      this.save().subscribe();
    });
  }

  openGenerator() {
    const data: OutcomeGeneratorData = {
      minScore: this.minScore,
      maxScore: this.maxScore,
      inputComponents: this.inputComponents,
    };

    const dialogRef = this.dialog.open(OutcomeGeneratorComponent, {
      width: '600px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((generatedOutcomes: Outcome[]) => {
      if (generatedOutcomes && generatedOutcomes.length > 0) {
        this.processGeneratedOutcomes(generatedOutcomes);
      }
    });
  }

  private processGeneratedOutcomes(generatedOutcomes: Outcome[]) {
    this.loading = true;
    this.createStatesForOutcomes(generatedOutcomes, 0, []);
  }

  private createStatesForOutcomes(generatedOutcomes: Outcome[], index: number, createdStates: WidgetState[]) {
    if (index >= generatedOutcomes.length) {
      this.finalizeGeneratedOutcomes(generatedOutcomes, createdStates);
      return;
    }

    const title = `Outcome ${this.outcomes.length + 1 + index}`;
    this.addState(title).subscribe((createdState) => {
      createdStates.push(createdState);
      this.createStatesForOutcomes(generatedOutcomes, index + 1, createdStates);
    });
  }

  private finalizeGeneratedOutcomes(generatedOutcomes: Outcome[], createdStates: WidgetState[]) {
    // Remove default outcome temporarily
    const defaultOutcome = this.outcomes.find((outcome) => outcome.isDefault);
    if (defaultOutcome) {
      this.outcomes = this.outcomes.filter((outcome) => !outcome.isDefault);
    }

    // Copy key from last outcome in list
    const previousCustomDataUpdateKey =
      this.outcomes.length > 0 ? this.outcomes[this.outcomes.length - 1]?.customDataUpdate?.key : '';

    generatedOutcomes.forEach((outcomeTemplate, i) => {
      const state = createdStates[i];
      const newOutcome: Outcome = {
        stateId: state.id,
        answers: outcomeTemplate.answers,
        isDefault: false,
        customDataUpdate: previousCustomDataUpdateKey ? { key: previousCustomDataUpdateKey, value: '' } : undefined,
      };
      this.outcomes.push(newOutcome);
    });

    // Add default outcome back
    if (defaultOutcome) {
      this.outcomes.push(defaultOutcome);
      this.reorderStates(createdStates, defaultOutcome.stateId, () => {
        this.finishSave();
      });
    } else {
      this.finishSave();
    }
  }

  private reorderStates(statesToMove: WidgetState[], targetStateId: number, completion: () => void) {
    if (statesToMove.length === 0) {
      completion();
      return;
    }

    const stateToMove = statesToMove[0];
    const moveToState = this.widget.states.find((state) => state.id === targetStateId);

    if (moveToState) {
      this.widgetEditorDataService.updateWidgetStateOrder(stateToMove.id, moveToState.order).subscribe(() => {
        this.widget.states = this.widgetEditorDataService.widget.states;
        this.reorderStates(statesToMove.slice(1), targetStateId, completion);
      });
    } else {
      this.reorderStates(statesToMove.slice(1), targetStateId, completion);
    }
  }

  private finishSave() {
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
    this.save().subscribe(() => {
      this.loading = false;
    });
  }

  addOutcome() {
    this.loading = true;
    // Create new state
    this.addState().subscribe((createdState) => {
      // Remove default outcome temporarily
      const defaultOutcome = this.outcomes.find((outcome) => outcome.isDefault);
      if (defaultOutcome) {
        this.outcomes = this.outcomes.filter((outcome) => !outcome.isDefault);
      }

      // Copy key from last outcome in list
      const previousCustomDataUpdateKey = this.outcomes[this.outcomes.length - 1]?.customDataUpdate?.key;

      // Add new regular outcome
      const outcome = {
        stateId: createdState.id,
        answers: [],
        isDefault: false,
        customDataUpdate: previousCustomDataUpdateKey ? { key: previousCustomDataUpdateKey, value: '' } : undefined,
      };
      this.addAnswer(outcome);
      this.outcomes.push(outcome);

      // Add default outcome back at the end and move state before the default outcome
      if (defaultOutcome) {
        this.outcomes.push(defaultOutcome);
        const moveToStateId = this.widget.states[this.widget.states.length - 2].id;
        this.moveStateToOutcomeOrder(createdState.id, moveToStateId);
      }

      this.expandedOutcomeIndex = this.outcomes.length - (this.hasDefaultOutcome() ? 2 : 1);

      this.calculateOutcomeProperties();
      this.calculateMissingScores();
      this.save().subscribe(() => {
        this.loading = false;
      });
    });
  }

  addAnswer(outcome: Outcome) {
    this.matchedOutcomeIndex = null;
    const question = this.inputComponents?.length ? this.inputComponents[0].name : '';
    let answer: OutcomeAnswer | undefined;
    if (outcome.answers && outcome.answers.length > 0) {
      answer = outcome.answers[outcome.answers.length - 1];
    } else {
      answer = this.getLastAnswer(this.outcomes);
    }

    outcome.answers.push({
      question: answer?.question ?? question,
      operator: answer?.operator ?? OutcomeOperator.Equals,
      andOrOperator: AndOrOperator.And,
      value: '',
      availableValues: [],
    });
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
  }

  private getLastAnswer(outcomes: Outcome[]): OutcomeAnswer {
    for (let i = outcomes.length - 1; i >= 0; i--) {
      const outcome = outcomes[i];
      if (outcome.answers && outcome.answers.length > 0) {
        return outcome.answers[outcome.answers.length - 1];
      }
    }

    return undefined;
  }

  onAndOrOperatorChange(answer: OutcomeAnswer, operator: AndOrOperator) {
    this.matchedOutcomeIndex = null;
    answer.andOrOperator = operator;
    this.save().subscribe();
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
  }

  duplicateOutcome(index: number) {
    this.loading = true;
    const outcome = this.outcomes[index];

    // No outcome expanded after duplication
    this.expandedOutcomeIndex = undefined;

    // Duplicate the state using widgetService
    this.widgetService.duplicateWidgetState(this.widget.id, outcome.stateId).subscribe((duplicatedState) => {
      // Add the duplicated state to widget states
      const stateIndex = this.widget.states.findIndex((state) => state.id === outcome.stateId);
      this.widget.states.splice(stateIndex + 1, 0, duplicatedState);
      this.availableStates.push({
        ...duplicatedState,
        titleState: WidgetComponentHelpers.pickTitleForState(duplicatedState, this.languageService),
      });

      // Create a deep copy of the outcome with the new state ID
      const duplicatedOutcome: Outcome = JSON.parse(JSON.stringify(outcome));
      duplicatedOutcome.stateId = duplicatedState.id;

      // Insert the duplicated outcome immediately after the original
      this.outcomes.splice(index + 1, 0, duplicatedOutcome);

      // Update orders and recalculate properties
      this.calculateOutcomeProperties();
      this.calculateMissingScores();

      // Save changes
      this.save().subscribe(() => {
        this.loading = false;
        this.logger.debug('Duplicated outcome', duplicatedOutcome);
      });
    });
  }

  removeOutcome(index: number) {
    this.confirmationDialogService
      .confirm(
        new ConfirmationDialogData(
          'confirmationDialog.confirmationTitle',
          'generic.confirmDeleteDialog.message',
          '',
          this.languageService.translateInstantly('campaign.outcomes.outcome'),
        ),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.loading = true;
        const outcome = this.outcomes[index];
        if (outcome.stateId) {
          this.widgetService.deleteWidgetState(this.widget.id, outcome.stateId).subscribe(() => {
            this.widget.states = this.widget.states.filter((state) => state.id !== outcome.stateId);

            // Change to another state if we are deleting the current state
            if (this.widgetEditorDataService.widgetState?.id === outcome.stateId) {
              const first = this.outcomes[0];
              const stateId = first && first.stateId !== outcome.stateId ? first.stateId : this.widget.states[0].id;
              const state = this.widget.states.find((state) => state.id === stateId);
              this.widgetEditorDataService.currentlyEditedComponentBS.next(undefined);
              this.widgetEditorDataService.widgetStateBS.next(state);
              this.widgetEditorDataService.resetVisibleItemIds();
            }
          });
        }

        this.outcomes.splice(index, 1);
        this.calculateOutcomeProperties();
        this.calculateMissingScores();
        this.save().subscribe(() => {
          this.loading = false;
        });
      });
  }

  removeAnswer(outcome: Outcome, index: number) {
    this.confirmationDialogService
      .confirm(
        new ConfirmationDialogData(
          'confirmationDialog.confirmationTitle',
          'generic.confirmDeleteDialog.message',
          '',
          this.languageService.translateInstantly('campaign.outcomes.question'),
        ),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.matchedOutcomeIndex = null;
        outcome.answers.splice(index, 1);
        this.save().subscribe();
        this.calculateOutcomeProperties();
        this.calculateMissingScores();
      });
  }

  save(): Observable<any> {
    // Deep clone outcomes to avoid modifying the original data
    const outcomesToSave = JSON.parse(JSON.stringify(this.outcomes));

    // Convert array values to strings for storage
    outcomesToSave.forEach((outcome) => {
      outcome.answers.forEach((answer) => {
        if (this.isMultipleSelect(answer) && Array.isArray(answer.value)) {
          answer.value = JSON.stringify(answer.value);
        }
      });
    });

    return this.widgetService.patchWidget(this.widget.id, 'outcomes', outcomesToSave).pipe(
      tap(() => {
        this.widget.outcomes = this.outcomes;
        this.widgetService.hasUnpublishedChangesBS.next(true);
        this.widgetEditorDataService.widgetBS.next(this.widget);
        this.logger.debug('Saved outcomes', this.outcomes);
      }),
    );
  }

  editOutcome(outcome: Outcome) {
    this.saveIsAllowed = false;
    this.save().subscribe(() => {
      this.dialogRef.close(outcome.stateId);
    });
  }

  activateCustomDataUpdate(outcome: Outcome) {
    outcome.customDataUpdate = { key: '', value: '' };
  }

  deactivateCustomDataUpdate(outcome: Outcome) {
    this.confirmationDialogService
      .confirm(
        new ConfirmationDialogData(
          'confirmationDialog.confirmationTitle',
          'generic.confirmDeleteDialog.message',
          '',
          this.languageService.translateInstantly('campaign.outcomes.update-profile-field'),
        ),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        outcome.customDataUpdate = undefined;
        this.save().subscribe();
      });
  }

  close() {
    this.saveIsAllowed = false;
    this.save().subscribe(() => {
      this.dialogRef.close();
    });
  }

  cancel() {
    this.saveIsAllowed = false;
    this.dialogRef.close();
  }

  onAnswerOperatorChange(answer: OutcomeAnswer) {
    if (this.isMultipleSelect(answer)) {
      answer.value = '[]';
    }
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
  }

  onValueChange() {
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
  }

  isMultipleSelect(answer: OutcomeAnswer) {
    return answer.operator === OutcomeOperator.Equals || answer.operator === OutcomeOperator.NotEquals;
  }

  onAnswerQuestionChange(answer: OutcomeAnswer) {
    const component = this.inputComponents.find((comp) => comp.name === answer.question);
    answer.availableValues = component?.values || [];
    // Reset value when question changes if it's not in the new available values
    if (answer.availableValues.length > 0 && !answer.availableValues.includes(answer.value.toString())) {
      answer.value = '';
    }
    this.calculateOutcomeProperties();
    this.calculateMissingScores();
  }

  editOutcomeName($event, outcome: Outcome) {
    $event?.stopPropagation();
    outcome.editName = true;
    setTimeout(() => {
      this.nameInputRef?.nativeElement?.focus();
    });
  }

  onOutcomeNameChange(outcome: Outcome) {
    outcome.editName = false;
    this.calculateOutcomeProperties();
    this.save().subscribe();
  }

  toggleTestLogic() {
    this.showTestLogic = !this.showTestLogic;
    if (this.showTestLogic) {
      this.dialogRef.updateSize('1150px', '80vh');
      this.matchedOutcomeIndex = null;
    } else {
      this.dialogRef.updateSize('800px', 'auto');
      this.matchedOutcomeIndex = null;
    }
  }

  onMatchedOutcomeIndexChange(index: number | null) {
    this.matchedOutcomeIndex = index;
  }

  closeTestLogic() {
    this.showTestLogic = false;
    this.matchedOutcomeIndex = null;
    this.dialogRef.updateSize('800px', 'auto');
  }

  public addState(title?: string): Observable<WidgetState> {
    const lastRegularState = this.availableStates
      .sort((a, b) => b.order - a.order)
      .find((state) => state.type === WidgetComponentType.Regular && !state.isInnerCanvasState);

    // Add new state
    const newState = CampaignTemplateHelpers.getCopyOfTemplateState(
      lastRegularState,
      WidgetComponentType.Regular,
      this.widget.states.length,
      CampaignTemplateHelpers.getDefaultStatePositionFromWidgetLayoutAndStateType(
        lastRegularState,
        WidgetComponentType.Regular,
      ),
      this.widget.brandVariables?.canvas,
    );

    newState.widgetId = this.widget.id;
    title = title ?? `Outcome ${this.widget.outcomes.length + 1}`;

    // Create the state
    return this.widgetService.createWidgetState(this.widget.id, newState).pipe(
      switchMap((createdState: WidgetState) => {
        this.widget.states.push(createdState);
        this.availableStates.push({
          ...createdState,
          titleState: title,
        });
        // Create a default-container for the state
        return this.widgetService
          .createWidgetContainer(this.widget.id, createdState.id, CampaignTemplateHelpers.getDefaultContainer(0))
          .pipe(
            map((container) => {
              createdState.containers.push(container);
              this.widgetEditorDataService.widgetStateBS.next(createdState);

              this.addDefaultTitleTextToOutcomeState(container, title);
              this.addDefaultTextToOutcomeState(container);
              this.addCloseButtonToOutcomeState(container);
              return createdState;
            }),
          );
      }),
    );
  }

  private moveStateToOutcomeOrder(moveFromStateId: number, moveToStateId: number) {
    const moveToState = this.widget.states.find((state) => state.id === moveToStateId);
    if (moveToState) {
      this.widgetEditorDataService.updateWidgetStateOrder(moveFromStateId, moveToState.order).subscribe(() => {
        this.widget.states = this.widgetEditorDataService.widget.states;
        this.save().subscribe();
      });
    }
  }

  private getProfileFields() {
    this.peopleService.getProfileFields().subscribe((items: CustomProfileProperty[]) => {
      if (items) {
        this.profileFields = items;
      }
    });
  }

  public onCreateNewField(outcome: Outcome) {
    const dialogRef = this.dialog.open(ProfileFieldEditingComponent, {
      panelClass: 'profile-field-editing-dialog',
      data: {
        value: null,
        isCreate: true,
        allProfileFields: this.profileFields,
      },
    });

    dialogRef.afterClosed().subscribe((result: CustomProfileProperty) => {
      if (result) {
        this.profileFields.push(result);
        outcome.customDataUpdate.key = result.mapTo;
      }
    });
  }

  private addDefaultTextToOutcomeState(container): void {
    // Add default text-component
    const component = WidgetComponentHelpers.createBlankWidgetComponentFromSubtype(
      this.widget,
      WidgetComponentSubType.TextRegular,
      this.languageService,
      false,
      null,
      undefined,
    );

    this.widgetEditorDataService
      .addComponentToWidgetState(component, 1, container.id, null, false, false, false, false, false, false)
      .subscribe(
        (createdComponent: WidgetComponent) => {
          this.logger.debug('Added component: ' + JSON.stringify(createdComponent));
        },
        (e) => {
          this.logger.error('Error while adding component: ' + e);
        },
      );
  }

  private addDefaultTitleTextToOutcomeState(container, title): void {
    // Add default text-component
    const component = WidgetComponentHelpers.createBlankWidgetComponentFromSubtype(
      this.widget,
      WidgetComponentSubType.TextRegular,
      this.languageService,
      false,
      null,
      undefined,
      null,
      this.accountLanguageService,
    );
    component.textProperties.text = `<h1>${title}</h1>`;

    this.widgetEditorDataService
      .addComponentToWidgetState(component, 0, container.id, null, false, false, false, false, false, false)
      .subscribe(
        (createdComponent: WidgetComponent) => {
          this.logger.debug('Added component: ' + JSON.stringify(createdComponent));
        },
        (e) => {
          this.logger.error('Error while adding component: ' + e);
        },
      );
  }

  private addCloseButtonToOutcomeState(container): void {
    const componentToAdd = WidgetComponentHelpers.createBlankWidgetComponentFromSubtype(
      this.widget,
      WidgetComponentSubType.ButtonHide,
      this.languageService,
      false,
      null,
      {
        name: WidgetComponentHelpers.generateWidgetComponentTextFieldName(
          this.widget,
          WidgetComponentSubType.ButtonHide,
        ),
      },
      undefined,
      undefined,
    );

    this.widgetEditorDataService
      .addComponentToWidgetState(componentToAdd, null, container.id, null, false, false, false, false, false, false)
      .subscribe(
        (createdComponent: WidgetComponent) => {
          this.logger.debug('Component added: ' + JSON.stringify(createdComponent));
        },
        () => {
          throw new Error('Button close should not be able to be added');
        },
      );
  }
  generateMissingOutcomes() {
    if (this.missingScores.length === 0) return;

    const ranges = this.outcomeLogicTesterService.getRanges(this.missingScores);

    if (ranges.length === 1) {
      // Simple case: Open generator for this range
      const range = ranges[0];
      const data: OutcomeGeneratorData = {
        minScore: range.start,
        maxScore: range.end,
        inputComponents: this.inputComponents,
      };

      const dialogRef = this.dialog.open(OutcomeGeneratorComponent, {
        width: '600px',
        data: data,
      });

      dialogRef.afterClosed().subscribe((generatedOutcomes: Outcome[]) => {
        if (generatedOutcomes && generatedOutcomes.length > 0) {
          this.processGeneratedOutcomes(generatedOutcomes);
        }
      });
    } else {
      // Complex case: Generate outcomes for each range
      const generatedOutcomes = this.outcomeLogicTesterService.generateOutcomesFromRanges(ranges);
      this.processGeneratedOutcomes(generatedOutcomes);
    }
  }

  private calculateMissingScores() {
    const result = this.outcomeLogicTesterService.calculateMissingScores(
      this.outcomes,
      this.scoreInput?.name || '',
      this.minScore,
      this.maxScore,
    );
    this.missingScoresRanges = result.missingScoresRanges;
    this.missingScoresIsDefault = result.missingScoresIsDefault;
    this.missingScores = result.missingScores;
    this.logicUpdateTrigger++;
  }

  trackByGroupStartIndex(_index: number, group: { answers: OutcomeAnswer[]; startIndex: number }): number {
    return group.startIndex;
  }

  trackByAnswer(_index: number, answer: OutcomeAnswer): OutcomeAnswer {
    return answer;
  }

  private getAnswerGroups(outcome: Outcome): { answers: OutcomeAnswer[]; startIndex: number }[] {
    const groups: { answers: OutcomeAnswer[]; startIndex: number }[] = [];
    let currentGroup: OutcomeAnswer[] = [];
    let groupStart = 0;

    outcome.answers.forEach((answer, i) => {
      currentGroup.push(answer);

      const isLast = i === outcome.answers.length - 1;
      // The operator on answers[i] determines how answers[i+1] joins.
      // An OR operator ends the current group.
      if (!isLast && answer.andOrOperator === AndOrOperator.Or) {
        groups.push({ answers: currentGroup, startIndex: groupStart });
        groupStart = i + 1;
        currentGroup = [];
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ answers: currentGroup, startIndex: groupStart });
    }

    return groups;
  }

  get hasUnconfiguredAnswerQuestions(): boolean {
    return this.outcomes.some((outcome) =>
      outcome.answers.some(
        (answer) => !answer.question || !this.inputComponents.some((c) => c.name === answer.question),
      ),
    );
  }

  get hasUnconfiguredAnswerValues(): boolean {
    return this.outcomes.some((outcome) => outcome.answers.some((answer) => !answer.value));
  }
}
